import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
  Paper,
  Alert,
  Snackbar,
  Divider,
  IconButton,
  useTheme,
  alpha,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Container,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormLabel,
  Badge,
  List as MuiList,
  ListItem as MuiListItem,
  ListItemText as MuiListItemText,
  ListItemSecondaryAction,
  ListItemAvatar,
  Avatar,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Image as ImageIcon,
  Visibility as VisibilityIcon,
  GetApp as GetAppIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Timeline as TimelineIcon,
  InfoOutlined as InfoIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Description as DescriptionIcon,
  DeleteOutline as DeleteOutlineIcon,
  InsertDriveFile as InsertDriveFileIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import workflowService from '../../../services/workflowService';

const CreateWorkflowRequestPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [workflowTemplates, setWorkflowTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({
    workflowTemplateId: '',
    title: '',
    description: '',
    customData: {},
    attachments: [],
  });
  const [errors, setErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Select Workflow Type', 'Enter Details', 'Review & Submit'];

  useEffect(() => {
    const fetchWorkflowTemplates = async () => {
      try {
        const response = await workflowService.getWorkflowTemplates();
        setWorkflowTemplates(response.data || []);
      } catch (err) {
        console.error('Error fetching workflow templates:', err);
        setError('Failed to load workflow templates. Please try again.');
      } finally {
        setInitialLoading(false);
      }
    };
    fetchWorkflowTemplates();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    if (name === 'workflowTemplateId') {
      const template = workflowTemplates.find((t) => t._id === value);
      setSelectedTemplate(template || null);
    }
  };

  const handleCustomFieldChange = (fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      customData: {
        ...prev.customData,
        [fieldName]: value,
      },
    }));
    const errorKey = `customData.${fieldName}`;
    if (errors[errorKey]) {
      setErrors((prev) => ({
        ...prev,
        [errorKey]: '',
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (activeStep === 0) {
      if (!formData.workflowTemplateId) {
        newErrors.workflowTemplateId = 'Please select a workflow template';
      }
    } else if (activeStep === 1) {
      if (!formData.title.trim()) {
        newErrors.title = 'Please provide a title for this workflow request';
      }
      if (selectedTemplate && selectedTemplate.formFields) {
        selectedTemplate.formFields.forEach((field) => {
          if (
            field.required &&
            (!formData.customData[field.name] ||
              (typeof formData.customData[field.name] === 'string' &&
                !formData.customData[field.name].trim()))
          ) {
            newErrors[`customData.${field.name}`] = `${field.label} is required`;
          }
        });
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleFileAttachment = (event) => {
    const files = Array.from(event.target.files);
    const maxSizeInBytes = 10 * 1024 * 1024; // 10MB limit
    const validFiles = files.filter((file) => file.size <= maxSizeInBytes);
    if (validFiles.length < files.length) {
      setError('Some files were not added because they exceed the 10MB size limit.');
    }
    setFormData((prev) => ({
      ...prev,
      attachments: [...prev.attachments, ...validFiles],
    }));
    if (errors.attachments) {
      setErrors((prev) => ({
        ...prev,
        attachments: '',
      }));
    }
    event.target.value = '';
  };

  const handleRemoveAttachment = (index) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const handleDownloadFile = (file) => {
    if (file.filename) {
      // If the file is already uploaded and has a filename from the server
      const fileUrl = workflowService.getAttachmentUrl(file.filename);

      // Option 1: Open in new tab
      window.open(fileUrl, '_blank');

      // Option 2: Handle with fetch and blob (more control over errors)
      // fetch(fileUrl, {
      //   headers: {
      //     'Authorization': `Bearer ${localStorage.getItem('token')}`,
      //     'x-tenant-id': localStorage.getItem('tenantId')
      //   }
      // })
      // .then(response => {
      //   if (!response.ok) {
      //     throw new Error('File download failed');
      //   }
      //   return response.blob();
      // })
      // .then(blob => {
      //   const url = URL.createObjectURL(blob);
      //   const a = document.createElement('a');
      //   a.href = url;
      //   a.download = file.originalName || file.filename;
      //   document.body.appendChild(a);
      //   a.click();
      //   setTimeout(() => {
      //     document.body.removeChild(a);
      //     URL.revokeObjectURL(url);
      //   }, 0);
      // })
      // .catch(err => {
      //   console.error('Error downloading file:', err);
      //   setError('Unable to download file. Please try again later.');
      // });
    } else {
      // For local files not yet uploaded, create a blob URL
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setError('');

    try {
      const requestFormData = new FormData();
      requestFormData.append('workflowTemplateId', formData.workflowTemplateId);
      requestFormData.append('title', formData.title);
      requestFormData.append('description', formData.description);
      requestFormData.append('formData', JSON.stringify(formData.customData));
      formData.attachments.forEach((file, index) => {
        requestFormData.append(`attachment${index}`, file);
      });

      await workflowService.createWorkflowRequestWithAttachments(requestFormData, (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress({
          loaded: progressEvent.loaded,
          total: progressEvent.total,
          percent: percentCompleted,
        });
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/member/workflows');
      }, 2000);
    } catch (err) {
      console.error('Error creating workflow request:', err);
      setError(err.message || 'An error occurred while submitting your request. Please try again.');
    } finally {
      setLoading(false);
      setUploadProgress({});
    }
  };

  const handleCloseSnackbar = () => {
    setSuccess(false);
  };

  const handleCancel = () => {
    navigate('/member/workflows');
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    if (['pdf'].includes(extension)) {
      return <PdfIcon color="error" />;
    } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) {
      return <ImageIcon color="primary" />;
    } else {
      return <InsertDriveFileIcon color="action" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const renderFormField = (field) => {
    const errorKey = `customData.${field.name}`;
    const hasError = !!errors[errorKey];
    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel':
      case 'url':
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.label}
            type={field.type}
            name={field.name}
            value={formData.customData[field.name] || ''}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            error={hasError}
            helperText={hasError ? errors[errorKey] : field.helpText}
            required={field.required}
            margin="normal"
          />
        );
      case 'number':
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.label}
            type="number"
            name={field.name}
            value={formData.customData[field.name] || ''}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            error={hasError}
            helperText={hasError ? errors[errorKey] : field.helpText}
            required={field.required}
            margin="normal"
            InputProps={{ inputProps: { min: field.min, max: field.max } }}
          />
        );
      case 'textarea':
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.label}
            multiline
            rows={4}
            name={field.name}
            value={formData.customData[field.name] || ''}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            error={hasError}
            helperText={hasError ? errors[errorKey] : field.helpText}
            required={field.required}
            margin="normal"
          />
        );
      case 'select':
        return (
          <FormControl
            key={field.name}
            fullWidth
            error={hasError}
            required={field.required}
            margin="normal"
          >
            <InputLabel id={`${field.name}-label`}>{field.label}</InputLabel>
            <Select
              labelId={`${field.name}-label`}
              name={field.name}
              value={formData.customData[field.name] || ''}
              onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
              label={field.label}
            >
              {field.options?.map((option) => (
                <MenuItem key={option.value || option} value={option.value || option}>
                  {option.label || option}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{hasError ? errors[errorKey] : field.helpText}</FormHelperText>
          </FormControl>
        );
      case 'checkbox':
        return (
          <FormControl
            key={field.name}
            fullWidth
            error={hasError}
            margin="normal"
          >
            <FormLabel component="legend">{field.label}{field.required ? ' *' : ''}</FormLabel>
            <FormGroup>
              {field.options?.map((option) => (
                <FormControlLabel
                  key={option.value || option}
                  control={
                    <Checkbox
                      checked={Array.isArray(formData.customData[field.name])
                        ? formData.customData[field.name].includes(option.value || option)
                        : false}
                      onChange={(e) => {
                        const currentValues = Array.isArray(formData.customData[field.name])
                          ? [...formData.customData[field.name]]
                          : [];
                        if (e.target.checked) {
                          handleCustomFieldChange(field.name, [...currentValues, option.value || option]);
                        } else {
                          handleCustomFieldChange(
                            field.name,
                            currentValues.filter(value => value !== (option.value || option))
                          );
                        }
                      }}
                    />
                  }
                  label={option.label || option}
                />
              ))}
            </FormGroup>
            <FormHelperText>{hasError ? errors[errorKey] : field.helpText}</FormHelperText>
          </FormControl>
        );
      case 'radio':
        return (
          <FormControl
            key={field.name}
            fullWidth
            error={hasError}
            required={field.required}
            margin="normal"
          >
            <FormLabel component="legend">{field.label}</FormLabel>
            <RadioGroup
              name={field.name}
              value={formData.customData[field.name] || ''}
              onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            >
              {field.options?.map((option) => (
                <FormControlLabel
                  key={option.value || option}
                  value={option.value || option}
                  control={<Radio />}
                  label={option.label || option}
                />
              ))}
            </RadioGroup>
            <FormHelperText>{hasError ? errors[errorKey] : field.helpText}</FormHelperText>
          </FormControl>
        );
      case 'date':
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.label}
            type="date"
            name={field.name}
            value={formData.customData[field.name] || ''}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            error={hasError}
            helperText={hasError ? errors[errorKey] : field.helpText}
            required={field.required}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        );
      default:
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.label}
            name={field.name}
            value={formData.customData[field.name] || ''}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            error={hasError}
            helperText={hasError ? errors[errorKey] : field.helpText}
            required={field.required}
            margin="normal"
          />
        );
    }
  };

  const renderFileAttachmentSection = () => {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle1" gutterBottom>
          Attachments
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            borderStyle: 'dashed',
            borderRadius: 2,
            textAlign: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.04)
          }}
        >
          <input
            type="file"
            id="file-upload"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileAttachment}
          />
          <label htmlFor="file-upload">
            <Button
              component="span"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              sx={{ mb: 1 }}
            >
              Upload Files
            </Button>
          </label>
          <Typography variant="caption" display="block" color="text.secondary">
            Drag and drop files here or click to browse. Maximum file size: 10MB
          </Typography>
        </Paper>
        
        {formData.attachments.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {formData.attachments.length} file(s) attached
            </Typography>
            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
              <MuiList disablePadding>
                {formData.attachments.map((file, index) => (
                  <MuiListItem
                    key={`${file.name}-${index}`}
                    divider={index < formData.attachments.length - 1}
                    sx={{ px: 2, py: 1 }}
                  >
                    <ListItemAvatar>
                      <Avatar 
                        variant="rounded" 
                        sx={{ bgcolor: 'background.paper', cursor: 'pointer' }}
                        onClick={() => handleDownloadFile(file)}
                      >
                        {getFileIcon(file.name)}
                      </Avatar>
                    </ListItemAvatar>
                    <MuiListItemText
                      primary={file.name}
                      secondary={formatFileSize(file.size)}
                      primaryTypographyProps={{ 
                        variant: 'body2', 
                        noWrap: true,
                        sx: { cursor: 'pointer' } 
                      }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                      onClick={() => handleDownloadFile(file)}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Download">
                        <IconButton 
                          edge="end"
                          onClick={() => handleDownloadFile(file)}
                          size="small"
                          sx={{ mr: 1 }}
                        >
                          <GetAppIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton 
                        edge="end" 
                        aria-label="delete" 
                        onClick={() => handleRemoveAttachment(index)}
                        size="small"
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </MuiListItem>
                ))}
              </MuiList>
            </Paper>
          </Box>
        )}
      </Box>
    );
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Workflow Type
            </Typography>
            <FormControl fullWidth error={!!errors.workflowTemplateId} sx={{ mb: 3 }}>
              <InputLabel id="workflow-label">Workflow Type</InputLabel>
              <Select
                labelId="workflow-label"
                id="workflowTemplateId"
                name="workflowTemplateId"
                value={formData.workflowTemplateId}
                onChange={handleChange}
                label="Workflow Type"
              >
                {workflowTemplates.map(template => (
                  <MenuItem key={template._id} value={template._id}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.workflowTemplateId ? (
                <FormHelperText error>{errors.workflowTemplateId}</FormHelperText>
              ) : (
                <FormHelperText>Select the workflow type for your request</FormHelperText>
              )}
            </FormControl>
            
            {formData.workflowTemplateId && selectedTemplate && (
              <Box sx={{ 
                mt: 2, 
                p: 2, 
                bgcolor: 'primary.lighter', 
                borderRadius: 2,
                border: '1px solid', 
                borderColor: 'primary.light' 
              }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>
                  {selectedTemplate.name}
                </Typography>
                <Typography variant="body2">
                  {selectedTemplate.description || 'No description provided for this workflow.'}
                </Typography>
                
                {selectedTemplate.steps && selectedTemplate.steps.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Workflow Steps:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {selectedTemplate.steps.map((step, index) => (
                        <Box 
                          key={step._id} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            p: 1,
                            borderRadius: 1,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          <Box 
                            sx={{ 
                              width: 24, 
                              height: 24, 
                              borderRadius: '50%', 
                              bgcolor: 'primary.main', 
                              color: 'white', 
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 14,
                              fontWeight: 'bold'
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Typography variant="body2">
                            {step.name}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
            <Box sx={{ 
              mt: 3, 
              p: 2, 
              bgcolor: 'info.lighter', 
              borderRadius: 2, 
              border: '1px solid', 
              borderColor: 'info.light'
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center' 
                }}
              >
                <InfoIcon color="info" fontSize="small" sx={{ mr: 1 }} />
                Please select the appropriate workflow type for your request. Each workflow type 
                has specific steps and requirements.
              </Typography>
            </Box>
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Enter Workflow Details
            </Typography>
            
            <TextField
              fullWidth
              required
              id="title"
              name="title"
              label="Title"
              value={formData.title}
              onChange={handleChange}
              error={!!errors.title}
              helperText={errors.title || "A brief title for this workflow request"}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              id="description"
              name="description"
              label="Description"
              multiline
              rows={4}
              value={formData.description}
              onChange={handleChange}
              error={!!errors.description}
              helperText={errors.description || "Provide additional context for your request (optional)"}
              sx={{ mb: 3 }}
            />
            
            {selectedTemplate && selectedTemplate.formFields && selectedTemplate.formFields.length > 0 ? (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Additional Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {selectedTemplate.formFields.map(field => renderFormField(field))}
              </Box>
            ) : (
              <Box sx={{ 
                p: 3, 
                borderRadius: 2, 
                bgcolor: 'grey.100',
                textAlign: 'center'
              }}>
                <DescriptionIcon color="disabled" sx={{ fontSize: 40, mb: 1 }} />
                <Typography color="text.secondary">
                  No additional fields required for this workflow
                </Typography>
              </Box>
            )}
            {renderFileAttachmentSection()}
            <Box sx={{ 
              mt: 3, 
              p: 2, 
              bgcolor: 'info.lighter', 
              borderRadius: 2, 
              border: '1px solid', 
              borderColor: 'info.light'
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center' 
                }}
              >
                <InfoIcon color="info" fontSize="small" sx={{ mr: 1 }} />
                Please fill in all required fields accurately. You can attach supporting documents 
                to provide additional context.
              </Typography>
            </Box>
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review & Submit
            </Typography>
            
            <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Workflow Type
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedTemplate?.name || 'Unknown workflow type'}
                  </Typography>
                </Box>
                {formData.description && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Description
                      </Typography>
                      <Typography variant="body1">
                        {formData.description}
                      </Typography>
                    </Box>
                  </>
                )}
                {selectedTemplate && selectedTemplate.formFields && selectedTemplate.formFields.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Additional Information
                    </Typography>
                    
                    <List disablePadding>
                      {selectedTemplate.formFields.map(field => (
                        <ListItem key={field.name} divider>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <InfoIcon fontSize="small" color="action" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={field.label}
                            secondary={
                              (() => {
                                const value = formData.customData[field.name];
                                if (value === undefined || value === null || value === '') {
                                  return 'Not provided';
                                }
                                
                                if (Array.isArray(value)) {
                                  return value.join(', ');
                                }
                                
                                if (field.type === 'date') {
                                  return new Date(value).toLocaleDateString();
                                }
                                
                                return String(value);
                              })()
                            }
                            primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                            secondaryTypographyProps={{ variant: 'body1' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
                
                {formData.attachments.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        <Badge 
                          badgeContent={formData.attachments.length} 
                          color="primary"
                          sx={{ '& .MuiBadge-badge': { fontSize: '10px', height: '16px', minWidth: '16px' } }}
                        >
                          <AttachFileIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                        </Badge>
                        Attachments
                      </Typography>
                      
                      <Box sx={{ pl: 2 }}>
                        {formData.attachments.map((file, index) => (
                          <Box 
                            key={index} 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              mb: 0.5,
                              py: 0.5,
                              cursor: 'pointer'
                            }}
                            onClick={() => handleDownloadFile(file)}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '16px', mr: 1 }}>
                              {getFileIcon(file.name)}
                            </Box>
                            <Typography variant="body2" noWrap sx={{ maxWidth: '80%' }}>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              ({formatFileSize(file.size)})
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
            
            <Box sx={{ 
              p: 2, 
              bgcolor: 'warning.lighter', 
              borderRadius: 2, 
              border: '1px solid', 
              borderColor: 'warning.light',
              mb: 3
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'flex-start' 
                }}
              >
                <InfoIcon color="warning" fontSize="small" sx={{ mr: 1, mt: 0.25 }} />
                Please review your workflow request details carefully before submitting. Once submitted, 
                your request will enter the workflow process and be reviewed according to the defined steps.
              </Typography>
            </Box>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <React.Fragment>
      <Box sx={{ py: 3, px: { xs: 2, md: 3 } }}>
        <Paper
          elevation={0}
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #4b6cb7 0%, #182848 100%)',
            color: 'white' 
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton 
              color="inherit" 
              onClick={handleCancel}
              sx={{ mr: 2, bgcolor: 'rgba(255,255,255,0.1)' }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" component="h1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                <TimelineIcon sx={{ mr: 1.5 }} />
                Create New Workflow Request
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.8, mt: 0.5 }}>
                Start a new workflow process for approval or action
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Card sx={{ 
          borderRadius: 3, 
          overflow: 'hidden',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          mb: 3
        }}>
          <CardContent sx={{ p: 4 }}>
            <Stepper 
              activeStep={activeStep} 
              alternativeLabel
              sx={{ mb: 5 }}
            >
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel
                    StepIconProps={{
                      sx: {
                        ...(index === 0 && { 
                          '& .MuiStepIcon-root': { color: 'primary.main' } 
                        }),
                        ...(index === 1 && { 
                          '& .MuiStepIcon-root': { color: 'secondary.main' } 
                        }),
                        ...(index === 2 && { 
                          '& .MuiStepIcon-root': { color: 'success.main' } 
                        }),
                      }
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            {initialLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box component="form" onSubmit={handleSubmit}>
                {getStepContent(activeStep)}
                {error && (
                  <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  mt: 4 
                }}>
                  <Button
                    variant="outlined"
                    onClick={activeStep === 0 ? handleCancel : handleBack}
                    disabled={loading}
                    sx={{ borderRadius: '8px' }}
                    startIcon={activeStep === 0 ? <ArrowBackIcon /> : undefined}
                  >
                    {activeStep === 0 ? 'Cancel' : 'Back'}
                  </Button>
                  
                  <Box>
                    {activeStep === steps.length - 1 ? (
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                        sx={{
                          borderRadius: '8px',
                          px: 3,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        }}
                      >
                        {loading ? 'Submitting...' : 'Submit Request'}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={handleNext}
                        sx={{ borderRadius: '8px' }}
                      >
                        Next
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
        
        <Card sx={{ 
          p: 3, 
          borderRadius: 3, 
          bgcolor: 'background.paperLight',
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <React.Fragment>
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  <InfoIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                  About Workflows
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Workflows are structured processes that guide requests through various stages of approval 
                  and action. Each workflow type is designed for a specific purpose with its own set of steps 
                  and requirements.
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  After submitting your request, you can track its progress through the workflow stages 
                  on the My Workflow Requests page. You may be required to provide additional information 
                  or take action at certain steps in the process.
                </Typography>
              </Box>
            </Box>
          </React.Fragment>
        </Card>
      </Box>
      
      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
          icon={<CheckCircleIcon />}
        >
          Your workflow request has been submitted successfully!
        </Alert>
      </Snackbar>
      
      {loading && Object.keys(uploadProgress).length > 0 && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Uploading files: {uploadProgress.percent}% complete
          </Typography>
          <LinearProgress
            variant="determinate"
            value={uploadProgress.percent}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      )}
    </React.Fragment>
  );
};

export default CreateWorkflowRequestPage;